import { Component, ElementRef, OnDestroy } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, AuthError, InteractionStatus } from '@azure/msal-browser';
import { service, factories, models, IEmbedConfiguration } from 'powerbi-client';
import { ReplaySubject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ActivatedRoute } from '@angular/router';
import { ReportsService } from '../reports.service';

@Component({
    selector: 'app-dynamic-report',
    templateUrl: './dynamic-report.component.html',
    styleUrls: ['./dynamic-report.component.css']
})
export class DynamicReportComponent implements OnDestroy {
  powerbi = new service.Service(factories.hpmFactory, factories.wpmpFactory, factories.routerFactory);
  state: any;
  reportContainer: any;
  context: any;
  accessToken: any;
  isAuthenticated: boolean = false; // Add a flag to track authentication status
  embedUrl: any;
  private readonly _destroying$: ReplaySubject<boolean> = new ReplaySubject(1);
  ReportName: any;
  ReportID: any = '';
  report: any;
  query: any;
  filterString: string;
  pagename: any = '';
  appliedfilter: any;
  HTML: string = ''; // Add this property to resolve the error
  constructor(
    private readonly msalService: MsalService,
    private readonly broadcastService: MsalBroadcastService,
    private readonly ContainerRef: ElementRef,
    private readonly reportsService: ReportsService,
    private readonly activatedRoute: ActivatedRoute
  ) {
    this.msalService.initialize();
    this.activatedRoute.queryParams.subscribe(data => {
      this.ReportName = decodeURIComponent(
        window.location.href.substring(window.location.href.lastIndexOf('/') + 1).split('?')[0]
      );
      this.query = data['query'];
      this.reportsService.getdynamicReport(this.ReportName).subscribe(result => {
        if (result.length > 0) {
          this.report = result[0];
          this.ReportID = this.report.Report_Id;
          this.filterurl();
          if (!this.isAuthenticated && !sessionStorage.getItem('ptoken')) {
            this.authenticate();
          } else {
            this.accessToken = sessionStorage.getItem('ptoken');
            this.isAuthenticated = true;
            this.getembedUrl(); // Proceed with fetching the embed URL if already authenticated
          }
        }
      });
    });
  }

  // React function
  render() {
    this.reportContainer = this.ContainerRef.nativeElement.querySelector('#reportContainer');
    this.reportContainer.innerHTML = '';
    const accessToken = this.accessToken;
    const embedUrl = this.embedUrl + this.filterString;
    const ReportID = this.ReportID;
    if (this.state?.error?.length) {
      // Cleaning the report container contents and rendering the error message in multiple lines
      this.state.error.forEach(line => {
        this.reportContainer.appendChild(document.createTextNode(line));
        this.reportContainer.appendChild(document.createElement('br'));
      });
    } else if (this.state.accessToken !== '' && this.state.embedUrl !== '') {
      const embedConfiguration: IEmbedConfiguration = {
        type: 'report',
        tokenType: models.TokenType.Aad,
        accessToken,
        embedUrl,
        permissions: models.Permissions.All,
        eventHooks: {
          accessTokenProvider: this.getNewAccessToken
        },
        id: ReportID,
        settings: {
          personalBookmarksEnabled: true,
          panes: {
            bookmarks: {
              visible: false
            },
            fields: {
              expanded: false
            },
            filters: {
              expanded: false,
              visible: false
            },
            pageNavigation: {
              visible: true
            },
            selection: {
              visible: true
            },
            syncSlicers: {
              visible: true
            },
            visualizations: {
              expanded: false
            }
          },
          bars: {
            actionBar: {
              visible: true
            },
            statusBar: {
              visible: true
            }
          }
        }
      };

      const report = this.powerbi.embed(this.reportContainer, embedConfiguration);

      // Clear any other loaded handler events
      report.off('loaded');

      // Triggers when a content schema is successfully loaded
      report.on('loaded', function () {
        console.log('Report load successful');
      });

      // Clear any other rendered handler events
      report.off('rendered');

      // Triggers when a content is successfully embedded in UI
      report.on('rendered', function () {
        console.log('Report render successful');
      });

      // Clear any other error handler event
      report.off('error');

      // Below patch of code is for handling errors that occur during embedding
      report.on('error', function (event) {
        const errorMsg = event.detail;

        // Use errorMsg variable to log error in any destination of choice
        console.error(errorMsg);
      });
    }
  }


  ngOnDestroy(): void {
    this.powerbi.reset(this.reportContainer);
  }

  // Authenticating to get the access token
  authenticate(): void {
    const msalInstance = this.msalService.instance;
    const msalAccounts = msalInstance.getAllAccounts();
    const msalInProgress = this.broadcastService.inProgress$;

    if (this.isAuthenticated) {
      console.log('Already authenticated. Skipping authentication.');
      return;
    }

    const loginRequest = {
      scopes: environment.scopeBase,
      account: msalAccounts[0]
    };

    msalInstance
      .handleRedirectPromise()
      .then(authResult => {
        if (authResult) {
          const payload = authResult as AuthenticationResult;
          this.accessToken = payload.accessToken;
          sessionStorage.setItem('ptoken', this.accessToken);
          this.isAuthenticated = true;
          this.setUsername(payload.account?.name || '');
          this.getembedUrl();
        } else if (!msalAccounts.length) {
          msalInstance.loginRedirect(loginRequest);
        }
      })
      .catch(err => {
        console.error('Authentication error:', err);
      });

    msalInProgress
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        if (!this.isAuthenticated && msalAccounts.length > 0) {
          msalInstance
            .acquireTokenSilent(loginRequest)
            .then((response: AuthenticationResult) => {
              this.accessToken = response.accessToken;
              sessionStorage.setItem('ptoken', this.accessToken);
              this.isAuthenticated = true;
              this.getembedUrl();
            })
            .catch((error: AuthError) => {
              if (
                error.errorCode === 'consent_required' ||
                error.errorCode === 'interaction_required' ||
                error.errorCode === 'login_required'
              ) {
                msalInstance.acquireTokenRedirect(loginRequest);
              } else {
                console.error('Error acquiring token silently:', error);
              }
            });
        }
      });
  }

  // Power BI REST API call to refresh User Permissions in Power BI
  // Refreshes user permissions and makes sure the user permissions are fully updated
  // https://docs.microsoft.com/rest/api/power-bi/users/refreshuserpermissions
  tryRefreshUserPermissions(): void {
    fetch(environment.powerBiApiUrl + 'v1.0/myorg/RefreshUserPermissions', {
      headers: {
        Authorization: 'Bearer ' + this.accessToken
      },
      method: 'POST'
    })
      .then(function (response) {
        if (response.ok) {
          console.log('User permissions refreshed successfully.');
        } else if (response.status === 429) {
          // Too many requests in one hour will cause the API to fail
          console.error('Permissions refresh will be available in up to an hour.');
        } else {
          console.error(response);
        }
      })
      .catch(function (error) {
        console.error('Failure in making API call.' + error);
      });
  }

  // Power BI REST API call to get the embed URL of the report
  getembedUrl() {
    const myorgReport = 'v1.0/myorg/reports/';
    const url = environment.powerBiApiUrl + myorgReport + this.ReportID;
    fetch(url, {
      headers: {
        Authorization: 'Bearer ' + this.accessToken
      },
      method: 'GET'
    })
      .then(response => {
        const errorMessage: string[] = [];
        errorMessage.push('Error occurred while fetching the embed URL of the report');
        errorMessage.push('Request Id: ' + response.headers.get('requestId'));
        response
          .json()
          .then(body => {
            console.log('Embed URL fetched successfully.', response);
            // Successful response
            if (response.ok) {
              this.embedUrl = body['embedUrl'];
              this.state = { accessToken: this.accessToken, embedUrl: this.embedUrl };
              this.render();
              this.checkAndReload();
            }
            // If error message is available
            else {
              errorMessage.push('Error ' + response.status + ': ' + (body.error?.code || 'Unknown error'));
              this.state = { error: errorMessage };
              this.render();
              this.checkAndReload();
              if (body.error?.code === 'TokenExpired') {
                this.getNewAccessToken();
              }
            }
          })
          .catch(e => {
            console.log(e);
            errorMessage.push('Error ' + response.status + ': An error has occurred');
            this.state = { error: errorMessage };
            if (e?.error?.code === 'TokenExpired') {
              this.getNewAccessToken();
            }
          });
      })
      .catch(error => {
        // Error in making the API call
        this.state = { error: error };
      });
  }

  // Show username in the UI
  setUsername(username: string): void {
    const welcome = document.getElementById('welcome');
    if (welcome !== null) welcome.innerText = 'Welcome, ' + username;
  }
  getNewAccessToken = async () => {
    const msalInstance = this.msalService.instance;
    const msalAccounts = msalInstance.getAllAccounts();
    const loginRequest = {
      scopes: environment.scopeBase,
      account: msalAccounts[0]
    };
    // get access token silently from cached id-token
    msalInstance
      .acquireTokenSilent(loginRequest)
      .then((response: AuthenticationResult) => {
        this.accessToken = response.accessToken;
        sessionStorage.setItem('ptoken', this.accessToken);
        this.getembedUrl();
      })
      .catch((error: AuthError) => {
        // Refresh access token silently from cached id-token
        // Makes the call to handleredirectcallback
        if (
          error.errorCode === 'consent_required' ||
          error.errorCode === 'interaction_required' ||
          error.errorCode === 'login_required'
        ) {
          msalInstance.acquireTokenRedirect(loginRequest);
        } else if (error.errorCode === '429') {
          console.log('Our Service Token Server (STS) is overloaded, please try again in sometime');
        } else {
          console.log('There was some problem fetching the access token' + error.toString());
        }
      });
  };
  filterurl() {
    try
    {
    function gettable(key, dbfilter) {
      const tables = dbfilter.split(',');
      let keytabls = {};
      for (let table of tables) {
        const [tableName, columnName] = table.split('/');
        keytabls[columnName] = table;
      }
      if (keytabls[key]) {
        return keytabls[key];
      }
      else {
        return key + '/' + key;
      }
    }

    let dbfilter = this.report.Column_Filter ? this.report.Column_Filter : '';

    if (!this.query) {
      console.log('Error: query is undefined');
      this.filterString = ''; // Set a default value to avoid further errors
      return;
    }

    let query;
    try {
      query = this.checkjson(this.query);
    } catch (error) {
      console.log('Error parsing query:', error);
      this.filterString = ''; // Set a default value to avoid further errors
      return;
    }
    function getValueFromSubstring(substring: any, string: any) {
      const values = string.split(',');
      for (const value of values) {
        if (value.includes(substring)) {
          return value.split('-')[1]?.trim();
        }
      }
      return null;
    }
    let filters = query.filters;
    this.appliedfilter = filters;
    this.pagename = query.pagename;
    let page = getValueFromSubstring(query.tabname, this.report.Tab_Id);
    let filterString = '';
    let columnmapping = {
      sv_Desc: 'SV_Desc',
      sv_desc: 'SV_Desc',
      services: 'SV_Desc',
      Billing_Segment: 'Billing_Segment',
      ownerId: 'CombinedOwners',
      owner: 'CombinedOwners',
      ask_global_id: 'App_Filter',
      apps: 'App_Filter',
      appId: 'App_Filter',
      service: 'SV_DESC',
      /**
       * PADU filter keys for year-based aggregation:
       * - Current_PADU: Current year PADU value
       * - T1_PADU to T6_PADU: Aggregated PADU values for years T1 to T6
       */
      Current_PADU:'App_PADU',
      T1_PADU:'Agg_PADU_T1',
      T2_PADU:'Agg_PADU_T2',
      T3_PADU:'Agg_PADU_T3',
      T4_PADU:'Agg_PADU_T4',
      T5_PADU:'Agg_PADU_T5',
      T6_PADU:'Agg_PADU_T6',
      SERVICE_LEVEL_OWNER: 'CombinedOwners',
      Technical_Owner: 'CombinedOwners',
      Business_Owner:'CombinedOwners'
    };

    for (let i = 0; i < filters.length; i++) {
      if (i === 0) filterString = '&filter=';
      else {
        filterString += ' and ';
      }
      let key = Object.keys(filters[i])[0];
      let value = this.checkjson(filters[i][key]);
      // get key details from dbfilter
      let table_coulmn_map = gettable(columnmapping[key] || key, dbfilter);
      if (table_coulmn_map) {
        if (Array.isArray(value)) {
          if (value.length === 1) {
            filterString += `${table_coulmn_map} eq '${value[0]}'`;
          } else {
            filterString += `${table_coulmn_map} in (${value.map(v => `'${v}'`).join(',')})`;
          }
        } else {
          filterString += `${table_coulmn_map} eq '${value}'`;
        }
      }
    }

    if (page) {
      this.filterString = filterString + `&pageName=${page}`;
    } else {
      this.filterString = filterString;
    }
    console.log(this.filterString);
  }
  catch (error) {
      console.error('Error in filterurl:', error);
      return null;
    }
}

  get objectKeys() {
    return Object.keys;
  }
  checkjson(val) {
    try {
      return JSON.parse(val);
    } catch (e) {
      return val;
    }
  }
  checkAndReload() {
    if (this.reportContainer && this.reportContainer.innerHTML.trim() === '') {
      const reloadCount = sessionStorage.getItem('reloadCount');
      if (!reloadCount) {
        sessionStorage.setItem('reloadCount', '1');
        location.reload();
      } else {
        sessionStorage.removeItem('reloadCount');
      }
    }
  }
}
